import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { listReports, getInsightSeries } from "../lib/api";
import { LIBRARY } from "../constants/testIds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ALL_VALUE = "__all__";

const Library = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const initialCat = searchParams.get("category") || "";

  const [q, setQ] = useState(initialQ);
  const [category, setCategory] = useState(initialCat);
  const [series, setSeries] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getInsightSeries().then(setSeries).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    listReports(params)
      .then((d) => { setReports(d); setLoading(false); })
      .catch(() => setLoading(false));
    const next = {};
    if (q) next.q = q;
    if (category) next.category = category;
    setSearchParams(next, { replace: true });
  }, [q, category, setSearchParams]);

  const list = useMemo(() => reports, [reports]);

  return (
    <Layout>
      <SEO
        title="Research Library | Steck Aviation Management"
        description="In-depth research and analysis on aircraft ownership costs, management structures, fractional aviation, and the private jet market—from Steck Aviation Management."
      />
      <div data-theme="paper" style={{ minHeight: "calc(100vh - 88px)" }}>
        <section className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20">

          {/* Page header */}
          <div className="font-mono-label text-[var(--blue-soft)] mb-5">Research</div>
          <h1 className="font-display text-5xl md:text-6xl text-[var(--ivory)] leading-[1.05] max-w-3xl">
            The complete library.
            <span className="text-[var(--ivory-dim)]">{" "}Filter, search, read.</span>
          </h1>
          <p className="mt-7 text-[var(--slate)] max-w-2xl text-lg leading-[1.8] font-light">
            Search the full research catalogue, or narrow by stream of inquiry.
          </p>

          {/* Search + filter row */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-7 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--slate-dim)]" size={16} />
              <input
                className="input-field pl-11"
                placeholder="Search reports, topics, sources…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid={LIBRARY.searchInput}
              />
            </div>
            <div className="md:col-span-5">
              <Select
                value={category || ALL_VALUE}
                onValueChange={(v) => setCategory(v === ALL_VALUE ? "" : v)}
              >
                <SelectTrigger
                  className="h-[48px] w-full bg-white border border-[#D4CFC5] text-[#0C0C0C] rounded-none px-4 text-sm font-sans hover:border-[#2251FF] focus:ring-1 focus:ring-[#2251FF] data-[placeholder]:text-[#8A8A8A]"
                  data-testid={LIBRARY.categorySelect}
                >
                  <SelectValue placeholder="All streams" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-[#D4CFC5] text-[#0C0C0C] rounded-none font-sans shadow-lg">
                  <SelectItem value={ALL_VALUE} className="text-sm text-[#0C0C0C] hover:bg-[#F0EDE6]">All streams</SelectItem>
                  {series.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      className="text-sm text-[#0C0C0C]"
                      data-testid={`library-cat-option-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="divider-thin mt-14" />

          {/* Results */}
          <div className="mt-12" data-testid={LIBRARY.resultsGrid}>
            {loading ? (
              <div className="font-mono-label text-[var(--slate)] py-20 text-center">Loading…</div>
            ) : list.length === 0 ? (
              <div className="font-mono-label text-[var(--slate)] py-20 text-center">
                No reports match this filter.
              </div>
            ) : (
              <ul className="divide-y divide-[#D4CFC5]">
                {list.map((r) => (
                  <li key={r.id}>
                    <Link
                      to={`/reports/${r.slug}`}
                      className="group block py-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-start hover:bg-black/[0.025] -mx-4 px-4 transition-colors"
                      data-testid={LIBRARY.card(r.slug)}
                    >
                      <div className="md:col-span-2 font-mono-label text-[var(--blue-soft)]">{r.category}</div>
                      <div className="md:col-span-7">
                        <h3 className="font-display text-2xl md:text-3xl text-[var(--ivory)] group-hover:text-[#2251FF] transition-colors leading-tight">
                          {r.title}
                        </h3>
                        {r.subtitle && (
                          <p className="font-serif-soft text-xl italic text-[var(--ivory-dim)] mt-3 leading-snug">
                            {r.subtitle}
                          </p>
                        )}
                        {r.topics?.length > 0 && (
                          <div className="mt-5 flex flex-wrap gap-2">
                            {r.topics.slice(0, 4).map((t) => (
                              <span key={t} className="chip">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="md:col-span-3 flex md:justify-end items-center">
                        <span className="font-mono-label text-[var(--slate)] inline-flex items-center gap-2 group-hover:text-[#002868] transition-colors">
                          Read article <ArrowRight size={14} />
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Library;
